import { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Plus, Save } from "lucide-react";
import { toast } from "sonner";
import { BRANDS, brandHasBlkltd } from "../lib/brands";

const RARITIES = ["Common", "Uncommon", "Rare", "Ultra Rare"];
const VARIANT_TYPES = ["Standard", "Chase", "BLKLTD", "Exclusive"];
const STATUSES = ["Released", "Pre-Order", "Announced", "Rumored"];

const emptyRelease = {
  khmg_number: "", release_name: "", vehicle: "", chassis: "",
  brand: "", series: "", variant_name: "", variant_type: "Standard",
  colorway: "", livery: "", rarity: "Common", base_model: "",
  is_chase: false, is_blkltd: false, chase_ratio: "",
  msrp: "", estimated_market_value: "", release_date: "",
  status: "Released", notes: "", upc: "", sku: "",
  is_preorder: false, preorder_url: "",
};

// Build a form-state object from a release doc. Single source of truth so the
// initial useState, the open-edit re-bind effect, and the dirty-comparison
// snapshot all stay in sync.
const formFromRelease = (release) => ({
  khmg_number: release.khmg_number || "",
  release_name: release.release_name || "",
  vehicle: release.vehicle || "",
  chassis: release.chassis || "",
  brand: release.brand || "",
  series: release.series || "",
  variant_name: release.variant_name || "",
  variant_type: release.variant_type || "Standard",
  colorway: release.colorway || "",
  livery: release.livery || "",
  rarity: release.rarity || "Common",
  base_model: release.base_model || "",
  is_chase: release.is_chase || false,
  is_blkltd: release.is_blkltd || false,
  chase_ratio: release.chase_ratio || "",
  msrp: release.msrp ?? "",
  estimated_market_value: release.estimated_market_value ?? "",
  release_date: release.release_date || "",
  status: release.status || "Released",
  notes: release.notes || "",
  upc: release.upc || "",
  sku: release.sku || "",
  is_preorder: release.is_preorder || false,
  preorder_url: release.preorder_url || "",
});

function FormField({ label, children }) {
  return (
    <div>
      <Label className="text-zinc-400 text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export default function AdminReleaseForm({ mode = "create", release = null, open, onOpenChange, onSuccess, prefill = null }) {
  const isEdit = mode === "edit" && release;
  const initialForm = useMemo(
    () => (isEdit ? formFromRelease(release) : { ...emptyRelease, ...(prefill || {}) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEdit, release?.release_id]
  );
  const [form, setForm] = useState(initialForm);
  // Snapshot baseline used to detect "dirty" form state. Refreshed every time
  // the dialog opens (so re-opening after a successful save starts clean).
  const baselineRef = useRef(initialForm);
  const [saving, setSaving] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  // Re-apply prefill each time the dialog opens (only in create mode).
  // Without this, the useState initializer above captures whatever prefill was passed
  // at first mount and never refreshes when a parent (e.g. BarcodeScanDialog) updates it.
  useEffect(() => {
    if (open && !isEdit && prefill) {
      const next = { ...emptyRelease, ...prefill };
      setForm(next);
      baselineRef.current = next;
    } else if (open && !isEdit && !prefill) {
      // Fresh "Add Release" — reset to empty so previous draft doesn't leak.
      setForm(emptyRelease);
      baselineRef.current = emptyRelease;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Re-bind form to the latest `release` prop every time the dialog opens
  // in edit mode. The useState initializer above only runs ONCE per mount —
  // without this effect, opening the dialog after a save (or after `release`
  // changed underneath) would show stale form values, and saving would
  // overwrite the DB with whatever was captured on first mount.
  useEffect(() => {
    if (open && isEdit && release) {
      const fresh = formFromRelease(release);
      setForm(fresh);
      baselineRef.current = fresh;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, release?.release_id, release?.is_preorder, release?.release_date]);

  // Dirty = current form differs from the baseline snapshot. JSON.stringify
  // is fine here — the form is a flat object of primitives.
  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(baselineRef.current),
    [form]
  );

  // Intercept Dialog close attempts. If the form has unsaved edits, prompt
  // before discarding. Saving + programmatic-open are always allowed through.
  const handleOpenChange = (next) => {
    if (next) {
      onOpenChange(true);
      return;
    }
    if (saving) return; // never close mid-save
    if (isDirty) {
      setConfirmCloseOpen(true);
      return;
    }
    onOpenChange(false);
  };

  const confirmDiscard = () => {
    setConfirmCloseOpen(false);
    onOpenChange(false);
  };

  const showBlkltd = brandHasBlkltd(form.brand);
  const update = (key, value) => setForm((prev) => {
    const next = { ...prev, [key]: value };
    // If brand changes away from a Kaido House family, clear BLKLTD flag (it's not meaningful elsewhere)
    if (key === "brand" && !brandHasBlkltd(value)) {
      next.is_blkltd = false;
      if (next.variant_type === "BLKLTD") next.variant_type = "Standard";
    }
    return next;
  });

  const handleSubmit = async () => {
    if (!form.brand.trim()) { toast.error("Brand is required"); return; }
    if (!form.khmg_number.trim()) { toast.error("Catalog # is required"); return; }
    if (!form.release_name.trim()) { toast.error("Release name is required"); return; }
    if (!form.vehicle.trim()) { toast.error("Vehicle is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      payload.msrp = payload.msrp ? parseFloat(payload.msrp) : null;
      payload.estimated_market_value = payload.estimated_market_value ? parseFloat(payload.estimated_market_value) : null;
      payload.chase_ratio = payload.chase_ratio || null;
      payload.release_date = payload.release_date || null;
      payload.notes = payload.notes || null;

      if (isEdit) {
        const updatePayload = {};
        for (const [k, v] of Object.entries(payload)) {
          if (v !== null && v !== "" && v !== undefined) updatePayload[k] = v;
        }
        // Boolean must round-trip even when false so admin can flip is_preorder off.
        updatePayload.is_preorder = !!form.is_preorder;
        await api.put(`/admin/releases/${release.release_id}`, updatePayload);
        toast.success("Release updated!");
      } else {
        await api.post("/admin/releases", payload);
        toast.success("Release created!");
      }
      // Reset baseline so the form is no longer "dirty" — close goes through
      // without prompting.
      baselineRef.current = form;
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {isEdit ? "Edit Release" : "Add New Release"}
              {isDirty && (
                <span
                  data-testid="admin-release-dirty-pill"
                  className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-200 border border-amber-500/40 font-semibold"
                  title="You have unsaved changes"
                >
                  unsaved
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
          {/* Row 0: Brand (required) — searchable, free-type */}
          <FormField label="Brand *">
            <Input
              data-testid="brand-input"
              list="release-brand-options"
              value={form.brand}
              onChange={(e) => update("brand", e.target.value)}
              placeholder="Hot Wheels, Mini GT, Kaido House, Tarmac Works…"
              className="bg-zinc-950 border-zinc-800 text-zinc-100"
            />
            <datalist id="release-brand-options">
              {BRANDS.map((b) => <option key={b} value={b} />)}
            </datalist>
            <p className="text-[10px] text-zinc-600 mt-1">{BRANDS.length - 1}+ brands searchable · or type your own</p>
          </FormField>

          {/* Row 1: Catalog # + Name */}
          <div className="grid grid-cols-3 gap-3">
            <FormField label="Catalog # *">
              <Input data-testid="khmg-input" value={form.khmg_number} onChange={(e) => update("khmg_number", e.target.value)} placeholder="HW-FYP04 / MGT00123 / KHMG289" className="bg-zinc-950 border-zinc-800 text-zinc-100" />
            </FormField>
            <div className="col-span-2">
              <FormField label="Release Name *">
                <Input data-testid="release-name-input" value={form.release_name} onChange={(e) => update("release_name", e.target.value)} placeholder="Datsun 510 Pro Street OG Green" className="bg-zinc-950 border-zinc-800 text-zinc-100" />
              </FormField>
            </div>
          </div>

          {/* Row 2: Vehicle + Chassis */}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Vehicle *">
              <Input data-testid="vehicle-input" value={form.vehicle} onChange={(e) => update("vehicle", e.target.value)} placeholder="Datsun 510" className="bg-zinc-950 border-zinc-800 text-zinc-100" />
            </FormField>
            <FormField label="Chassis">
              <Input value={form.chassis} onChange={(e) => update("chassis", e.target.value)} placeholder="Datsun 510" className="bg-zinc-950 border-zinc-800 text-zinc-100" />
            </FormField>
          </div>

          {/* Row 3: Series + Variant Name */}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Series">
              <Input value={form.series} onChange={(e) => update("series", e.target.value)} placeholder="e.g. Kaido House, Premium, RLC" className="bg-zinc-950 border-zinc-800 text-zinc-100" />
            </FormField>
            <FormField label="Variant Name">
              <Input value={form.variant_name} onChange={(e) => update("variant_name", e.target.value)} placeholder="OG Green / Super Treasure Hunt" className="bg-zinc-950 border-zinc-800 text-zinc-100" />
            </FormField>
          </div>

          {/* Row 4: Variant Type + Rarity */}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Variant Type">
              <Select value={form.variant_type} onValueChange={(v) => update("variant_type", v)}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-300"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {VARIANT_TYPES.filter((t) => t !== "BLKLTD" || showBlkltd).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Rarity">
              <Select value={form.rarity} onValueChange={(v) => update("rarity", v)}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-300"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {RARITIES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
          </div>

          {/* Row 5: Colorway + Livery */}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Colorway">
              <Input value={form.colorway} onChange={(e) => update("colorway", e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-100" />
            </FormField>
            <FormField label="Livery">
              <Input value={form.livery} onChange={(e) => update("livery", e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-100" />
            </FormField>
          </div>

          {/* Row 6: Chase / BLKLTD toggles (BLKLTD only shown for Kaido House family) */}
          <div className="flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-2">
              <Switch data-testid="chase-toggle" checked={form.is_chase} onCheckedChange={(v) => update("is_chase", v)} />
              <Label className="text-zinc-300 text-sm">Chase</Label>
            </div>
            {showBlkltd && (
              <div className="flex items-center gap-2" data-testid="blkltd-section">
                <Switch data-testid="blkltd-toggle" checked={form.is_blkltd} onCheckedChange={(v) => update("is_blkltd", v)} />
                <Label className="text-zinc-300 text-sm">BLKLTD</Label>
              </div>
            )}
            {form.is_chase && (
              <FormField label="Chase Ratio">
                <Input value={form.chase_ratio} onChange={(e) => update("chase_ratio", e.target.value)} placeholder="1:48" className="bg-zinc-950 border-zinc-800 text-zinc-100 w-24" />
              </FormField>
            )}
          </div>

          {/* Row 7: Pricing */}
          <div className="grid grid-cols-3 gap-3">
            <FormField label="MSRP ($)">
              <Input type="number" step="0.01" value={form.msrp} onChange={(e) => update("msrp", e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-100" />
            </FormField>
            <FormField label="Est. Market Value ($)">
              <Input type="number" step="0.01" value={form.estimated_market_value} onChange={(e) => update("estimated_market_value", e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-100" />
            </FormField>
            <FormField label="Status">
              <Select value={form.status} onValueChange={(v) => update("status", v)}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-300"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
          </div>

          {/* Row 8: Date + Base Model */}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Release Date">
              <Input type="date" value={form.release_date} onChange={(e) => update("release_date", e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-100" />
            </FormField>
            <FormField label="Base Model">
              <Input value={form.base_model} onChange={(e) => update("base_model", e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-100" />
            </FormField>
          </div>

          {/* Row 9: UPC + SKU side by side */}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="UPC / EAN (barcode)">
              <Input
                data-testid="upc-input"
                value={form.upc}
                onChange={(e) => update("upc", e.target.value.replace(/[^0-9]/g, "").slice(0, 13))}
                placeholder="8 / 12 / 13 digits (optional)"
                className="bg-zinc-950 border-zinc-800 text-zinc-100 font-mono"
                maxLength={13}
              />
            </FormField>
            <FormField label="Manufacturer SKU">
              <Input
                data-testid="sku-input"
                value={form.sku}
                onChange={(e) => update("sku", e.target.value)}
                placeholder="e.g. HKC75 / FYP04"
                className="bg-zinc-950 border-zinc-800 text-zinc-100 font-mono"
                maxLength={64}
              />
            </FormField>
          </div>

          {/* Row 9.5: Preorder toggle + URL */}
          <div className="rounded-xl bg-zinc-950/60 border border-zinc-800 px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="text-zinc-200 text-sm font-semibold flex items-center gap-2">
                  <span className="text-fuchsia-300">◐</span> Mark as preorder
                </Label>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  Releases also auto-show in /preorders if the date above is in the future.
                </p>
              </div>
              <Switch
                data-testid="preorder-toggle"
                checked={form.is_preorder}
                onCheckedChange={(v) => update("is_preorder", v)}
              />
            </div>
            {(form.is_preorder || (form.release_date && new Date(form.release_date) > new Date())) && (
              <div className="mt-3">
                <Label className="text-zinc-400 text-xs">Preorder URL (optional)</Label>
                <Input
                  data-testid="preorder-url-input"
                  type="url"
                  value={form.preorder_url}
                  onChange={(e) => update("preorder_url", e.target.value)}
                  placeholder="https://kaidohouse.com/products/..."
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1"
                />
              </div>
            )}
          </div>

          {/* Notes */}
          <FormField label="Notes">
            <Input value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Any additional notes..." className="bg-zinc-950 border-zinc-800 text-zinc-100" />
          </FormField>

          <Button data-testid="save-release-btn" onClick={handleSubmit} disabled={saving} className="w-full bg-rose-600 hover:bg-rose-500 text-white">
            {saving ? "Saving..." : isEdit ? <><Save className="w-4 h-4 mr-1" /> Update Release</> : <><Plus className="w-4 h-4 mr-1" /> Add Release</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

      <AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="admin-release-discard-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              You've edited this release but haven't saved yet. Closing now will lose your changes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="admin-release-discard-cancel"
              className="bg-zinc-800 border-zinc-700 text-zinc-100 hover:bg-zinc-700 hover:text-white"
            >
              Keep editing
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-release-discard-confirm-btn"
              onClick={confirmDiscard}
              className="bg-rose-600 hover:bg-rose-500 text-white"
            >
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
